//#region IMPORT
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import dayjs from 'dayjs';
import { apiRequest } from '../../../Services/Common/ApiRequest';
//#endregion

/**
 * 예산 편성 및 집행 - 안전보건관리 예산편성(세부) (BUD_BASIC_02) 전용 로직
 * other 케이스에서 사용
 */

// BUD_BASIC_02 조회 함수 (데이터 처리 로직)
export const SearchLogic1 = async (apiUrl, procedureParams, dispatch, setLoading) => {
  setLoading(true);
  try {
    const result = await apiRequest(
      'post',
      apiUrl,
      procedureParams
    );

    let data = result.data;

    // data가 배열이 아닌 경우 배열로 변환
    if (!Array.isArray(data)) {
      data = data ? [data] : [];
    }

    if (data.length === 0) {
      dispatch(showSnackbar({ message: '데이터가 없습니다.', type: 'info' }));
      return [];
    } else if (data[0]?.type === 'Error') {
      dispatch(showSnackbar({ message: data[0].message, type: 'error' }));
      return [];
    } else {
      // 원본 데이터 매핑
      const mappedData = data.map((item, index) => ({
        ...item,
        no: index + 1,
        budCd: item.budId || '',
        remark: item.remark != null ? item.remark : '',
      }));

      // categoryId로 정렬하여 동일한 값끼리 그룹화
      const sortedData = mappedData.sort((a, b) => {
        const categoryIdA = a.categoryId != null ? Number(a.categoryId) : 0;
        const categoryIdB = b.categoryId != null ? Number(b.categoryId) : 0;
        if (categoryIdA === categoryIdB) {
          // 같은 구분일 경우 itemName으로 정렬
          const itemA = a.itemName || '';
          const itemB = b.itemName || '';
          return itemA.localeCompare(itemB);
        }
        return categoryIdA - categoryIdB;
      });

      // 정렬 후 각 행의 연간 예산총액 계산
      const monthFields = [
        'month01',
        'month02',
        'month03',
        'month04',
        'month05',
        'month06',
        'month07',
        'month08',
        'month09',
        'month10',
        'month11',
        'month12',
      ];

      sortedData.forEach((item) => {
        const totalYearAmount = monthFields.reduce((sum, field) => {
          const v = item[field];
          const num =
            typeof v === 'number' ? v : v != null && v !== '' ? Number(v) : 0;
          return sum + (isNaN(num) ? 0 : num);
        }, 0);

        item.totalYearAmount = totalYearAmount;
      });

      // categoryId별로 그룹화하여 합계 행 추가
      const finalData = [];
      const categoryGroups = new Map();

      // categoryId별로 그룹화
      sortedData.forEach((item) => {
        const categoryId = item.categoryId != null ? item.categoryId : '';
        if (!categoryGroups.has(categoryId)) {
          categoryGroups.set(categoryId, []);
        }
        categoryGroups.get(categoryId).push(item);
      });

      // 각 그룹별로 데이터와 합계 행 추가
      let rowIndex = 1;
      categoryGroups.forEach((groupItems, categoryId) => {
        // 그룹 내 각 행 추가
        groupItems.forEach((item) => {
          item.no = rowIndex++;
          finalData.push(item);
        });

        // 그룹별 합계 행 생성
        const groupPrevYearAmount = groupItems.reduce(
          (sum, row) => sum + (parseFloat(row.prevYearAmount) || 0),
          0
        );

        const groupPrevExecAmount = groupItems.reduce(
          (sum, row) => sum + (parseFloat(row.prevExecAmount) || 0),
          0
        );

        const groupPrevExecRate =
          groupPrevYearAmount > 0
            ? parseFloat(
              ((groupPrevExecAmount / groupPrevYearAmount) * 100).toFixed(2)
            )
            : 0;

        const groupMonthTotals = {};
        monthFields.forEach((field) => {
          groupMonthTotals[field] = groupItems.reduce(
            (sum, row) => sum + (parseFloat(row[field]) || 0),
            0
          );
        });

        const groupTotalYearAmount = Object.values(groupMonthTotals).reduce(
          (sum, val) => sum + val,
          0
        );

        const summaryRow = {
          no: rowIndex++,
          categoryId: categoryId,
          categoryName: '합계',
          itemName: '',
          prevYearAmount: groupPrevYearAmount,
          prevExecAmount: groupPrevExecAmount,
          prevExecRate: groupPrevExecRate,
          ...groupMonthTotals,
          totalYearAmount: groupTotalYearAmount,
          remark: '',
          isSummaryRow: true, // 합계 행 표시용 플래그
        };

        finalData.push(summaryRow);
      });

      return finalData;
    }
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return [];
  } finally {
    setLoading(false);
  }
};

// 분기별/반기별 합계 계산 함수
const calcQuarterHalfTotals = (row) => {
  const m = (key) => parseFloat(row[key]) || 0;

  return {
    q1Total: m('month01') + m('month02') + m('month03'),
    q2Total: m('month04') + m('month05') + m('month06'),
    q3Total: m('month07') + m('month08') + m('month09'),
    q4Total: m('month10') + m('month11') + m('month12'),

    h1Total:
      m('month01') +
      m('month02') +
      m('month03') +
      m('month04') +
      m('month05') +
      m('month06'),

    h2Total:
      m('month07') +
      m('month08') +
      m('month09') +
      m('month10') +
      m('month11') +
      m('month12'),
  };
};

// BUD_BASIC_02 인쇄 함수
export const PrintLogic1 = async (
  documentData, // documentData는 사용하지 않지만 시그니처 통일을 위해 유지
  dispatch,
  setLoading,
  rowData1,
  searchRef
) => {
  setLoading(true);
  const filtered = rowData1.filter((r) => r.itemName !== '합계');

  try {
    if (!rowData1 || rowData1.length === 0) {
      dispatch(
        showSnackbar({
          message: '인쇄할 데이터가 없습니다.',
          type: 'warning',
        })
      );
      setLoading(false);
      return false;
    }

    const currentYear =
      searchRef?.current?.currentYear || dayjs().format('YYYY');
    const previousYear =
      searchRef?.current?.previousYear ||
      (parseInt(currentYear) - 1).toString();
    const nextYear = (parseInt(currentYear) + 1).toString();
    const todayDate = dayjs().format('YYYY-MM-DD');

    // 천단위 구분 기호 추가 함수
    const formatNumber = (value) => {
      if (value == null || value === '') return '0';
      const num = typeof value === 'number' ? value : Number(value);
      return isNaN(num) ? '0' : num.toLocaleString('ko-KR');
    };

    // 합계 데이터 계산 (숫자 값 먼저 계산)
    const prevYearAmount = filtered.reduce(
      (sum, row) => sum + (parseFloat(row.prevYearAmount) || 0),
      0
    );

    const prevExecAmount = filtered.reduce(
      (sum, row) => sum + (parseFloat(row.prevExecAmount) || 0),
      0
    );

    const prevExecRate =
      prevYearAmount !== 0 ? prevExecAmount / prevYearAmount : 0;

    const totalYearAmount = filtered.reduce(
      (sum, row) => sum + (parseFloat(row.totalYearAmount) || 0),
      0
    );

    const summaryData = {
      summaryPrevYearAmount: formatNumber(prevYearAmount),
      summaryPrevExecAmount: formatNumber(prevExecAmount),
      summaryPrevExecRate: formatNumber(prevExecRate),
      summaryMonth01: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month01) || 0), 0)
      ),
      summaryMonth02: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month02) || 0), 0)
      ),
      summaryMonth03: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month03) || 0), 0)
      ),
      summaryMonth04: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month04) || 0), 0)
      ),
      summaryMonth05: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month05) || 0), 0)
      ),
      summaryMonth06: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month06) || 0), 0)
      ),
      summaryMonth07: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month07) || 0), 0)
      ),
      summaryMonth08: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month08) || 0), 0)
      ),
      summaryMonth09: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month09) || 0), 0)
      ),
      summaryMonth10: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month10) || 0), 0)
      ),
      summaryMonth11: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month11) || 0), 0)
      ),
      summaryMonth12: formatNumber(
        filtered.reduce((sum, row) => sum + (parseFloat(row.month12) || 0), 0)
      ),
      summaryTotalYearAmount: formatNumber(totalYearAmount),
      // 분기별/반기별 합계
      summaryQ1Total: formatNumber(
        filtered.reduce(
          (sum, r) =>
            sum +
            (parseFloat(r.month01) || 0) +
            (parseFloat(r.month02) || 0) +
            (parseFloat(r.month03) || 0),
          0
        )
      ),
      summaryQ2Total: formatNumber(
        filtered.reduce(
          (sum, r) =>
            sum +
            (parseFloat(r.month04) || 0) +
            (parseFloat(r.month05) || 0) +
            (parseFloat(r.month06) || 0),
          0
        )
      ),
      summaryQ3Total: formatNumber(
        filtered.reduce(
          (sum, r) =>
            sum +
            (parseFloat(r.month07) || 0) +
            (parseFloat(r.month08) || 0) +
            (parseFloat(r.month09) || 0),
          0
        )
      ),
      summaryQ4Total: formatNumber(
        filtered.reduce(
          (sum, r) =>
            sum +
            (parseFloat(r.month10) || 0) +
            (parseFloat(r.month11) || 0) +
            (parseFloat(r.month12) || 0),
          0
        )
      ),
      summaryH1Total: formatNumber(
        filtered.reduce(
          (sum, r) =>
            sum +
            (parseFloat(r.month01) || 0) +
            (parseFloat(r.month02) || 0) +
            (parseFloat(r.month03) || 0) +
            (parseFloat(r.month04) || 0) +
            (parseFloat(r.month05) || 0) +
            (parseFloat(r.month06) || 0),
          0
        )
      ),
      summaryH2Total: formatNumber(
        filtered.reduce(
          (sum, r) =>
            sum +
            (parseFloat(r.month07) || 0) +
            (parseFloat(r.month08) || 0) +
            (parseFloat(r.month09) || 0) +
            (parseFloat(r.month10) || 0) +
            (parseFloat(r.month11) || 0) +
            (parseFloat(r.month12) || 0),
          0
        )
      ),
    };

    const headerData = {
      currentYear: currentYear,
      previousYear: previousYear,
      nextYear: nextYear,
      todayDate: todayDate,
      custCd: searchRef?.current?.custCd || '',
      ...summaryData,
    };

    // 일반 행 데이터 포맷팅
    const regularData = filtered.map((row) => {
      const totals = calcQuarterHalfTotals(row);
      return {
        ...row,
        // 구분, 구분상세 명시적 포함
        categoryName: row.categoryName || '',
        itemName: row.itemName || '',
        // 년도 포함
        currentYear: currentYear,
        previousYear: previousYear,
        nextYear: nextYear,
        // 작성일자 포함
        createDate: todayDate,
        // 숫자 형식 포맷팅 (Number로 변환)
        prevYearAmount: Number(row.prevYearAmount),
        prevExecAmount: Number(row.prevExecAmount),
        prevExecRate: Number(row.prevExecRate),
        month01: Number(row.month01),
        month02: Number(row.month02),
        month03: Number(row.month03),
        month04: Number(row.month04),
        month05: Number(row.month05),
        month06: Number(row.month06),
        month07: Number(row.month07),
        month08: Number(row.month08),
        month09: Number(row.month09),
        month10: Number(row.month10),
        month11: Number(row.month11),
        month12: Number(row.month12),
        totalYearAmount: Number(row.totalYearAmount),
        // 비고 포함
        remark: row.remark || '',
        q1Total: Number(totals.q1Total),
        q2Total: Number(totals.q2Total),
        q3Total: Number(totals.q3Total),
        q4Total: Number(totals.q4Total),
        h1Total: Number(totals.h1Total),
        h2Total: Number(totals.h2Total),
      };
    });

    // 모든 데이터 합치기 (일반 행만)
    const tableData = regularData;

    const printData = {
      FileName: 'BUD_BASIC_02',
      HeaderData: JSON.stringify(headerData),
      TableData: JSON.stringify(tableData),
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    // response.data가 blob인 경우 그대로 반환
    if (response.data instanceof Blob) {
      return response.data;
    }
    // response.data가 다른 형태인 경우 blob으로 변환
    else if (response.data) {
      return new Blob([response.data], { type: 'application/pdf' });
    } else {
      console.error('PrintLogic1: 응답 데이터가 없습니다.');
      return null;
    }
  } catch (e) {
    console.log(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
  } finally {
    setLoading(false);
  }
};

// BUD_BASIC_02 예산안 인쇄 함수 (PrintLogic3)
export const PrintLogic3 = async (
  documentData, // documentData는 사용하지 않지만 시그니처 통일을 위해 유지
  dispatch,
  setLoading,
  rowData2, // 예산안 데이터 (rowData2 사용)
  searchRef
) => {
  setLoading(true);
  try {
    if (!rowData2 || rowData2.length === 0) {
      dispatch(
        showSnackbar({
          message: '인쇄할 데이터가 없습니다.',
          type: 'warning',
        })
      );
      setLoading(false);
      return null;
    }

    const currentYear =
      searchRef?.current?.currentYear || dayjs().format('YYYY');
    const previousYear =
      searchRef?.current?.previousYear ||
      (parseInt(currentYear) - 1).toString();
    const nextYear = (parseInt(currentYear) + 1).toString();
    const todayDate = dayjs().format('YYYY-MM-DD');

    // 천단위 구분 기호 추가 함수
    const formatNumber = (value) => {
      if (value == null || value === '') return '0';
      const num = typeof value === 'number' ? value : Number(value);
      return isNaN(num) ? '0' : num.toLocaleString('ko-KR');
    };

    // 합계 데이터 계산 (BUD_BASIC_03 레포트 구조에 맞게)
    // 2025년 결산: prevExecAmount (집행액)
    const prevYearSettlement = rowData2.reduce(
      (sum, row) => sum + (parseFloat(row.prevExecAmount) || 0),
      0
    );

    // 2026년 예산: totalYearAmount (연간 예산 총액)
    const nextYearBudget = rowData2.reduce(
      (sum, row) => sum + (parseFloat(row.totalYearAmount) || 0),
      0
    );

    const summaryData = {
      summaryPrevYearSettlement: formatNumber(prevYearSettlement), // 2025년 결산 합계
      summaryNextYearBudget: formatNumber(nextYearBudget), // 2026년 예산 합계
    };

    const headerData = {
      currentYear: currentYear,
      previousYear: previousYear,
      nextYear: nextYear,
      todayDate: todayDate,
      custCd: searchRef?.current?.custCd || '',
      ...summaryData,
    };

    // 일반 행 데이터 포맷팅 (BUD_BASIC_03 레포트 구조에 맞게)
    const regularData = rowData2.map((row) => ({
      ...row,
      // 구분, 구분상세 명시적 포함
      categoryName: row.categoryName || '',
      itemName: row.itemName || '',
      // 년도 포함
      currentYear: currentYear,
      previousYear: previousYear,
      nextYear: nextYear,
      // 작성일자 포함
      createDate: todayDate,
      // 2025년 결산: prevExecAmount (집행액)
      prevYearSettlement: formatNumber(row.prevExecAmount),
      // 2026년 예산: totalYearAmount (연간 예산 총액)
      nextYearBudget: formatNumber(row.totalYearAmount),
      // 비고 포함
      remark: row.remark || '',
    }));

    // 모든 데이터 합치기 (일반 행만)
    const tableData = regularData;

    const printData = {
      FileName: 'BUD_BASIC_03', // 예산안 레포트 파일명
      HeaderData: JSON.stringify(headerData),
      TableData: JSON.stringify(tableData),
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    // response.data가 blob인 경우 그대로 반환
    if (response.data instanceof Blob) {
      return response.data;
    }
    // response.data가 다른 형태인 경우 blob으로 변환
    else if (response.data) {
      return new Blob([response.data], { type: 'application/pdf' });
    } else {
      console.error('PrintLogic3: 응답 데이터가 없습니다.');
      return null;
    }
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return null;
  } finally {
    setLoading(false);
  }
};
